import React from 'react';
import Image from "next/image";
import Link from "next/link";
import { MinusIcon, PlusIcon, TrashIcon } from "@/components/ui/Icons";
import { getIp } from "@/utils/getCountryIp";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { useCartStore2 } from "@/stores/cartStore";

interface CartItemModalProps {
  product: any & { qty: number };
}

export default function CartItemModal({ product }: CartItemModalProps) {
  const { updateItemQty, removeItem, error, loading } = useCartStore2();
  const [country, setCountry] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    async function fetchCountry() {
      const countryCode = await getIp();
      setCountry(countryCode);
    }
    fetchCountry();
  }, []);

  useEffect(() => {
    if (error) {
      setIsUpdating(false);
      setIsRemoving(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: error,
      });
    }
  }, [error]);

  // Cuando loading cambia a false, resetear los estados de actualización
  useEffect(() => {
    if (!loading) {
      setIsUpdating(false);
      setIsRemoving(false);
    }
  }, [loading]);

  const isUSD = country !== "CL" && country !== null;

  const formatPrice = (price: number) => {
    return price.toLocaleString(isUSD ? "en-US" : "es-CL", {
      style: "currency",
      currency: isUSD ? "USD" : "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleUpdateQuantity = async (newQuantity: number) => {
    if (newQuantity > 0) {
      setIsUpdating(true);
      await updateItemQty(product.cart_item_id || product.id, newQuantity);
    }
  };

  const handleRemoveItem = async () => {
    setIsRemoving(true);
    await removeItem(product.cart_item_id || product.id);
  };

  // Si el item está siendo eliminado, mostrar un skeleton
  if (isRemoving) {
    return (
      <div className="flex justify-between py-4 border-b animate-pulse">
        <div className="flex gap-5 items-center">
          <div className="w-20 h-20 bg-gray-200 rounded-md"></div>
          <div className="flex flex-col items-start justify-between gap-2">
            <div>
              <div className="h-6 bg-gray-200 rounded-sm w-40 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded-sm w-32"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded-sm w-24 mt-2"></div>
          </div>
        </div>
        <div className="flex flex-col justify-between items-end">
          <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
          <div className="text-right">
            <div className="h-5 bg-gray-200 rounded-sm w-20"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between py-4 border-b">
      <div className="flex gap-5 items-center">
        <Link href={product.variant && product.variant.length > 0 && product.variant[0].id 
            ? `/products/${product.slug}?variant=${product.variant[0].id}` 
            : `/products/${product.slug}`}>
          <Image
            src={product.thumbnail_url}
            alt={product.name}
            className="w-20 h-20 object-cover border border-gray-200 rounded-md"
            width={80}
            height={80}
          />
        </Link>
        <div className="flex flex-col items-start justify-between gap-2">
          <div>
            <Link href={product.variant && product.variant.length > 0 && product.variant[0].id 
            ? `/products/${product.slug}?variant=${product.variant[0].id}` 
            : `/products/${product.slug}`}>
              <h3 className="text-lg font-semibold text-black uppercase">
                {product.name}
              </h3>
            </Link>
            {/* Mostrar variantes para productos normales */}
            {Array.isArray(product.variant) && product.variant.length > 0 && (
              <small className="font-semibold text-gray-500 mt-2">
                {product.variant.map((v: any) => `${v.name}: ${v.value}`).join(" / ")}
              </small>
            )}
            
            {/* Mostrar productos del bundle con sus variantes */}
            {product.type === 'bundle' && product.bundleProducts && product.bundleProducts.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-semibold text-gray-700">Productos incluidos:</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-2">
                  {product.bundleProducts.map((bundleItem: any, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-6 h-6 relative shrink-0 mt-0.5">
                        <Image 
                          src={bundleItem.variant?.thumbnail_url || bundleItem.product?.thumbnail_url || '/icon.svg'} 
                          alt={bundleItem.product?.name || 'Producto'}
                          className="rounded-xs object-cover border border-gray-200"
                          fill
                          sizes="24px"
                        />
                      </div>
                      <div>
                        <span className="font-medium block">{bundleItem.product?.name}</span>
                        {bundleItem.variant?.selectedOptions && bundleItem.variant.selectedOptions.length > 0 && (
                          <span className="text-gray-500 block">
                            {bundleItem.variant.selectedOptions.map((opt: any) => 
                              `${opt.variation?.name || ''}: ${opt.option?.value || ''}`
                            ).join(" / ")}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {product.type !== "subscription" && product.type !== "virtual" && product.type !== "bundle" && (
            <div className="flex gap-2">
              {isUpdating ? (
                <div className="flex gap-2 items-center">
                  <div className="h-7 w-7 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="h-5 w-5 bg-gray-200 rounded-sm animate-pulse"></div>
                  <div className="h-7 w-7 bg-gray-200 rounded-full animate-pulse"></div>
                </div>
              ) : (
                <>
                  <button
                    className="bg-gray-200 rounded-full p-1.5"
                    onClick={() => handleUpdateQuantity(product.qty - 1)}
                    aria-label="Decrease quantity"
                    disabled={product.qty <= 1}
                  >
                    <MinusIcon className="fill-black w-3 h-3" />
                  </button>
                  <span className="text-black">{product.qty}</span>
                  <button
                    className="bg-gray-200 rounded-full p-1.5"
                    onClick={() => handleUpdateQuantity(product.qty + 1)}
                    aria-label="Increase quantity"
                  >
                    <PlusIcon className="fill-black w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          )}
          {product.type === "bundle" && (
            <div className="flex gap-2">
              <span className="text-black text-sm bg-gray-100 px-2 py-1 rounded">Cantidad: {product.qty}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col justify-between items-end">
        <button
          className="bg-red-600 p-1.5 rounded-full"
          onClick={handleRemoveItem}
          aria-label="Remove item"
          disabled={isRemoving || isUpdating}
        >
          <TrashIcon className="fill-cream w-3 h-3" />
        </button>
        <div className="text-right">
          {product.discounted_price ? (
            <div className="flex flex-col items-end">
              <span className="text-gray-500 line-through text-xs">
                {formatPrice(product.price * product.qty)}
              </span>
              <span className="text-bright-pink font-medium">
                {formatPrice(product.discounted_price * product.qty)}
              </span>
            </div>
          ) : null}
          {!product.discounted_price && (
            <span className="text-black font-medium">
              {formatPrice(product.price * product.qty)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

